import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Phone, User, Clock, AlertCircle, Plus, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { getAuthToken } from '@/utils/api';
import { jwtDecode } from 'jwt-decode';

const ReservationsPage = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeTab, setActiveTab] = useState('confirmed');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmOverlap, setConfirmOverlap] = useState(null);
  
  const notifiedReservations = useRef(new Set()); // To keep track of already notified approaching reservations

  const [newRes, setNewRes] = useState({
    customer_name: '',
    customer_phone: '',
    table_number: '',
    reservation_date: new Date().toISOString().split('T')[0],
    reservation_time: '19:00'
  });

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchData();
    const interval = setInterval(() => {
      fetchData(true);
    }, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchData = async (isBackground = false) => {
    try {
      const [resResponse, tablesResponse] = await Promise.all([
        apiClient.get('/reservations'),
        apiClient.get('/tables')
      ]);
      const fetchedReservations = resResponse.data;
      setReservations(fetchedReservations);
      setTables(tablesResponse.data);
      
      checkApproachingReservations(fetchedReservations);
    } catch (error) {
      if (!isBackground) console.error('Veriler yüklenirken hata:', error);
    }
  };

  const checkApproachingReservations = (allReservations) => {
    const now = new Date();
    allReservations.forEach(res => {
      if (res.status === 'confirmed') {
        const resDateTimeStr = `${res.reservation_date}T${res.reservation_time}:00`;
        const resDate = new Date(resDateTimeStr);
        
        // Calculate difference in minutes
        const diffMs = resDate - now;
        const diffMins = Math.round(diffMs / 60000);
        
        // If reservation is exactly within 15 minutes and we haven't notified yet
        if (diffMins > 0 && diffMins <= 15 && !notifiedReservations.current.has(res.id)) {
          toast.warning(`Yaklaşan Rezervasyon: Masa ${res.table_number}`, {
            description: `${res.customer_name} adlı müşterinin rezervasyonuna ${diffMins} dakika kaldı!`,
            duration: 10000,
            icon: '⏰',
            className: 'bg-orange-50 dark:bg-slate-900 border-orange-500 text-orange-900 dark:text-orange-100'
          });
          notifiedReservations.current.add(res.id);
        }
      }
    });
  };

  const handleCreateReservation = async (e, skipWarning = false) => {
    if (e) e.preventDefault();
    if (!newRes.customer_name || !newRes.table_number || !newRes.reservation_date || !newRes.reservation_time) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    if (!skipWarning) {
      const tableNum = parseInt(newRes.table_number);
      const existingSameDay = reservations.filter(
        r => r.status === 'confirmed' && r.table_number === tableNum && r.reservation_date === newRes.reservation_date
      );

      if (existingSameDay.length > 0) {
        const exactTime = existingSameDay.find(r => r.reservation_time === newRes.reservation_time);
        if (exactTime) {
          toast.error(`Bu masada saat ${newRes.reservation_time} için zaten aktif bir rezervasyon var!`);
          return;
        }

        setConfirmOverlap({
          times: existingSameDay.map(r => r.reservation_time).join(', ')
        });
        return;
      }
    }

    try {
      setIsSubmitting(true);
      await apiClient.post('/admin/reservations', {
        ...newRes,
        table_number: parseInt(newRes.table_number)
      });
      toast.success('Rezervasyon başarıyla oluşturuldu!');
      setIsAddModalOpen(false);
      setConfirmOverlap(null);
      fetchData();
      // Reset form
      setNewRes({
        customer_name: '',
        customer_phone: '',
        table_number: '',
        reservation_date: new Date().toISOString().split('T')[0],
        reservation_time: '19:00'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rezervasyon oluşturulamadı.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReservationStatus = async (id, status) => {
    if (status === 'cancelled' && !window.confirm('Bu rezervasyonu iptal etmek istediğinize emin misiniz?')) {
      return;
    }
    try {
      await apiClient.put(`/reservations/${id}/status`, { status });
      toast.success(`Rezervasyon durumu '${status === 'completed' ? 'Tamamlandı' : 'İptal Edildi'}' olarak güncellendi`);
      fetchData();
    } catch (error) {
      toast.error('Rezervasyon durumu güncellenirken hata oluştu');
    }
  };

  const confirmedReservations = reservations.filter(r => r.status === 'confirmed');
  const pastReservations = reservations.filter(r => r.status === 'cancelled' || r.status === 'completed');
  const displayedReservations = activeTab === 'confirmed' ? confirmedReservations : pastReservations;

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              İç Rezervasyon Yönetimi
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">İşletmenizin masa rezervasyonlarını manuel olarak oluşturun ve takip edin.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl shadow-inner gap-1">
              <Button
                onClick={() => setActiveTab('confirmed')}
                className={`px-5 py-2.5 rounded-lg font-bold text-sm transition-all ${activeTab === 'confirmed' ? 'bg-orange-600 text-white shadow-lg shadow-orange-600/20' : 'bg-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}
              >
                Aktif Rezervasyonlar ({confirmedReservations.length})
              </Button>
              <Button
                onClick={() => setActiveTab('past')}
                className={`px-5 py-2.5 rounded-lg font-bold text-sm transition-all ${activeTab === 'past' ? 'bg-slate-600 text-white shadow-lg' : 'bg-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'}`}
              >
                Geçmiş / İptal ({pastReservations.length})
              </Button>
            </div>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 rounded-xl shadow-lg shadow-green-600/20 flex items-center gap-2"
            >
              <Plus size={18} />
              Yeni Ekle
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedReservations.map((res) => (
            <Card key={res.id} className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-6 hover:border-orange-500/30 transition-all shadow-xl rounded-2xl flex flex-col justify-between">
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-slate-100 tracking-tight">Masa {res.table_number}</h3>
                    <div className="flex items-center text-slate-600 dark:text-slate-400 text-sm mt-1 gap-1.5 font-medium">
                      <Calendar size={16} className="text-orange-500" />
                      <span className="text-orange-500 dark:text-orange-400 font-bold">{res.reservation_date}</span> - <span>{res.reservation_time}</span>
                    </div>
                  </div>
                  <Badge className={`border px-3 py-1 text-xs font-bold rounded-full ${
                    res.status === 'confirmed' ? 'bg-orange-500/20 text-orange-600 border-orange-500/30' :
                    res.status === 'cancelled' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                    'bg-slate-500/20 text-slate-400 border-slate-500/30'
                  }`}>
                    {res.status === 'confirmed' ? 'Aktif Rezervasyon' : res.status === 'cancelled' ? 'İptal Edildi' : 'Tamamlandı'}
                  </Badge>
                </div>

                <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-950 rounded-xl mb-6 border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center text-slate-700 dark:text-slate-300 text-sm gap-2.5">
                    <User size={18} className="text-indigo-500" />
                    <span className="font-bold tracking-wide">{res.customer_name}</span>
                  </div>
                  <div className="flex items-center text-slate-600 dark:text-slate-400 text-sm gap-2.5">
                    <Phone size={18} className="text-green-500" />
                    <span className="font-mono font-bold text-green-600 dark:text-green-400">{res.customer_phone || 'Belirtilmedi'}</span>
                  </div>
                  <div className="flex items-center text-xs text-slate-500 pt-3 border-t border-slate-200 dark:border-slate-800 gap-1.5">
                    <Clock size={14} />
                    <span>Oluşturulma: {new Date(res.created_at).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              </div>

              <div>
                {res.status === 'confirmed' && (
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleReservationStatus(res.id, 'completed')}
                      className="flex-1 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-bold py-6 rounded-xl border border-slate-300 dark:border-slate-700"
                    >
                      Sonlandır (Müşteri Geldi)
                    </Button>
                    <Button
                      onClick={() => handleReservationStatus(res.id, 'cancelled')}
                      className="bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white font-bold py-6 px-4 rounded-xl transition-all"
                      title="İptal Et"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                )}
                {(res.status === 'cancelled' || res.status === 'completed') && (
                  <div className="p-3 bg-slate-100 dark:bg-slate-900 text-slate-500 text-center text-xs font-bold rounded-xl border border-slate-200 dark:border-slate-800">
                    {res.status === 'cancelled' ? 'Bu rezervasyon iptal edildi.' : 'Bu rezervasyon tamamlandı.'}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {displayedReservations.length === 0 && (
          <Card className="bg-white dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 p-16 text-center space-y-4 rounded-2xl shadow-xl">
            <div className="w-16 h-16 bg-orange-500/10 border border-orange-500/20 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={32} className="text-orange-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">
              {activeTab === 'confirmed' ? 'Aktif Rezervasyon Bulunmuyor' : 'Geçmiş veya İptal Edilmiş Kayıt Yok'}
            </h2>
            <p className="text-slate-500 text-sm max-w-md mx-auto">
              Yeni rezervasyon eklemek için "Yeni Ekle" butonunu kullanabilirsiniz.
            </p>
          </Card>
        )}

        {/* Add Reservation Modal */}
        {isAddModalOpen && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-white dark:bg-slate-900 border-0 shadow-2xl rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
              <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800">
                <h3 className="text-xl font-black text-slate-900 dark:text-white">Yeni Rezervasyon Ekle</h3>
                <button onClick={() => { setIsAddModalOpen(false); setConfirmOverlap(null); }} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                  <X size={24} />
                </button>
              </div>
              <CardContent className="p-6">
                {confirmOverlap ? (
                  <div className="space-y-4 text-center py-4">
                    <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">⚠️</span>
                    </div>
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white">Çakışan Rezervasyon Uyarısı</h4>
                    <p className="text-slate-600 dark:text-slate-300">
                      Masa {newRes.table_number} için bugün ({newRes.reservation_date.split('-').reverse().join('.')}) saat <strong>{confirmOverlap.times}</strong>'de başka bir rezervasyon bulunuyor.
                    </p>
                    <p className="text-slate-600 dark:text-slate-300 font-medium mt-2">
                      Yine de bu yeni rezervasyonu kaydetmek istiyor musunuz?
                    </p>
                    <div className="flex gap-3 pt-4">
                      <Button variant="outline" className="flex-1" onClick={() => setConfirmOverlap(null)}>İptal Et</Button>
                      <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={(e) => handleCreateReservation(e, true)}>Evet, Kaydet</Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleCreateReservation} className="space-y-4">
                    <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Müşteri Adı *</label>
                    <input
                      type="text"
                      value={newRes.customer_name}
                      onChange={e => setNewRes({...newRes, customer_name: e.target.value})}
                      required
                      className="w-full h-10 px-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Telefon Numarası</label>
                    <input
                      type="tel"
                      value={newRes.customer_phone}
                      onChange={e => setNewRes({...newRes, customer_phone: e.target.value})}
                      placeholder="İsteğe bağlı"
                      className="w-full h-10 px-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Masa Seçimi *</label>
                    <select
                      value={newRes.table_number}
                      onChange={e => setNewRes({...newRes, table_number: e.target.value})}
                      required
                      className="w-full h-10 px-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                    >
                      <option value="">Masa Seçin</option>
                      {tables.map(t => (
                        <option key={t.id} value={t.table_number}>Masa {t.table_number}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Tarih *</label>
                      <input
                        type="date"
                        value={newRes.reservation_date}
                        onChange={e => setNewRes({...newRes, reservation_date: e.target.value})}
                        required
                        className="w-full h-10 px-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Saat *</label>
                      <input
                        type="time"
                        value={newRes.reservation_time}
                        onChange={e => setNewRes({...newRes, reservation_time: e.target.value})}
                        required
                        className="w-full h-10 px-3 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="pt-4 flex gap-3">
                    <Button type="button" onClick={() => setIsAddModalOpen(false)} className="flex-1 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-700">İptal</Button>
                    <Button type="submit" disabled={isSubmitting} className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold">
                      {isSubmitting ? 'Kaydediliyor...' : 'Oluştur'}
                    </Button>
                  </div>
                </form>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      </div>
    </AdminLayout>
  );
};

export default ReservationsPage;
